import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/common";

const GOALS = ["Lose weight", "Build muscle", "Get stronger", "Improve mobility", "Run a 5k", "General health"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    phone: "", height_ft: "", height_in: "", current_weight: "", target_weight: "", experience: "Beginner",
    primary_goal: "Get stronger", environment: "Gym", equipment: "", session_duration: 45,
    preferred_days: ["Mon", "Wed", "Fri"], injuries: "None", medical: "",
    nutrition_goals: "", sleep: "", coaching_style: "Supportive", communication: "In-app", emergency_contact: "",
  });
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleDay = (d) => set("preferred_days", data.preferred_days.includes(d) ? data.preferred_days.filter((x) => x !== d) : [...data.preferred_days, d]);

  const steps = [
    { title: "About you", fields: (
      <>
        <Field label="Mobile phone"><input data-testid="ob-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Height (ft)"><input data-testid="ob-height-ft" type="number" value={data.height_ft} onChange={(e) => set("height_ft", e.target.value)} className={inp} /></Field>
            <Field label="(in)"><input data-testid="ob-height-in" type="number" value={data.height_in} onChange={(e) => set("height_in", e.target.value)} className={inp} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Weight (lbs)"><input data-testid="ob-weight" type="number" value={data.current_weight} onChange={(e) => set("current_weight", e.target.value)} className={inp} /></Field>
            <Field label="Target (lbs)"><input data-testid="ob-target" type="number" value={data.target_weight} onChange={(e) => set("target_weight", e.target.value)} className={inp} /></Field>
          </div>
        </div>
        <Field label="Fitness experience">
          <select data-testid="ob-experience" value={data.experience} onChange={(e) => set("experience", e.target.value)} className={inp}>
            {["Beginner", "Intermediate", "Advanced"].map((x) => <option key={x}>{x}</option>)}
          </select>
        </Field>
      </>
    )},
    { title: "Your goals", fields: (
      <>
        <Field label="Primary goal">
          <div className="grid grid-cols-2 gap-2">
            {GOALS.map((g) => (
              <button key={g} type="button" data-testid={`ob-goal-${g}`} onClick={() => set("primary_goal", g)}
                className={`px-3 py-2.5 rounded-lg text-sm border transition-all ${data.primary_goal === g ? "border-brand bg-brand/10 text-white" : "border-border text-secondary hover:border-white/30"}`}>{g}</button>
            ))}
          </div>
        </Field>
        <Field label="Where will you train?">
          <select data-testid="ob-env" value={data.environment} onChange={(e) => set("environment", e.target.value)} className={inp}>
            {["Home", "Gym", "Outdoors", "Mixed"].map((x) => <option key={x}>{x}</option>)}
          </select>
        </Field>
        <Field label="Available equipment"><input data-testid="ob-equipment" placeholder="e.g. Dumbbells, bands" value={data.equipment} onChange={(e) => set("equipment", e.target.value)} className={inp} /></Field>
      </>
    )},
    { title: "Schedule & preferences", fields: (
      <>
        <Field label="Preferred workout days">
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <button key={d} type="button" data-testid={`ob-day-${d}`} onClick={() => toggleDay(d)}
                className={`px-3 py-2 rounded-lg text-sm border transition-all ${data.preferred_days.includes(d) ? "border-brand bg-brand/10" : "border-border text-secondary"}`}>{d}</button>
            ))}
          </div>
        </Field>
        <Field label="Typical session length">
          <select data-testid="ob-duration" value={data.session_duration} onChange={(e) => set("session_duration", Number(e.target.value))} className={inp}>
            {[30, 45, 60, 75].map((x) => <option key={x} value={x}>{x} minutes</option>)}
          </select>
        </Field>
        <Field label="Preferred coaching style">
          <select data-testid="ob-style" value={data.coaching_style} onChange={(e) => set("coaching_style", e.target.value)} className={inp}>
            {["Supportive", "Tough love", "Data-driven", "Balanced"].map((x) => <option key={x}>{x}</option>)}
          </select>
        </Field>
      </>
    )},
    { title: "Health & safety", fields: (
      <>
        <Field label="Injuries or limitations"><textarea data-testid="ob-injuries" value={data.injuries} onChange={(e) => set("injuries", e.target.value)} className={inp} rows={2} /></Field>
        <Field label="Medical considerations"><textarea data-testid="ob-medical" value={data.medical} onChange={(e) => set("medical", e.target.value)} className={inp} rows={2} /></Field>
        <Field label="Emergency contact"><input data-testid="ob-emergency" value={data.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} className={inp} /></Field>
        <p className="text-tertiary text-xs leading-relaxed bg-warning/10 border border-warning/20 rounded-lg p-3">
          This information helps your coach train you safely. Iron Mountain does not provide medical advice — please consult a medical professional before starting.
        </p>
      </>
    )},
  ];

  const submit = async () => {
    try {
      const payload = { ...data, height_ft: Number(data.height_ft) || null, height_in: Number(data.height_in) || null, current_weight: Number(data.current_weight) || null, target_weight: Number(data.target_weight) || null };
      await api.post("/onboarding", payload);
      await refresh();
      toast.success("You're all set! Welcome to the team.");
      navigate("/app");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    }
  };

  const isLast = step === steps.length - 1;
  return (
    <div className="min-h-screen bg-[#0A0A0A] py-10 px-5">
      <div className="max-w-lg mx-auto">
        <Logo />
        <div className="flex gap-2 mt-8">
          {steps.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-brand" : "bg-white/10"}`} />)}
        </div>
        <h1 className="font-heading text-4xl mt-8">{steps[step].title}</h1>
        <div className="space-y-5 mt-6">{steps[step].fields}</div>
        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <button data-testid="ob-back" onClick={() => setStep(step - 1)} className="px-5 py-3 border border-white/20 rounded-lg font-semibold flex items-center gap-2 hover:border-white transition-all">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
          <button data-testid="ob-next" onClick={() => (isLast ? submit() : setStep(step + 1))}
            className="flex-1 py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover transition-all flex items-center justify-center gap-2">
            {isLast ? "Finish & Enter App" : "Continue"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full bg-surface-elevated border border-border rounded-lg px-4 py-3 focus:outline-none focus:border-brand transition-colors";
function Field({ label, children }) {
  return <div><label className="label-xs block mb-2">{label}</label>{children}</div>;
}
